// 헤더 "🖼 이미지에서 글자 뽑기" — 전 화면 공통 이미지→텍스트 전사 도구 (티켓 #436)
//
// 상단 띠의 통합검색 오른쪽에 늘 떠 있는 버튼. 누르면 작은 창이 뜨고, 창이 열린 즉시 어디서든
// Ctrl+V 로 붙여넣거나 이미지를 끌어다 놓으면 안의 글자를 읽어 보여준다. 파일에서 고르기는
// 점선 박스 오른쪽 아래 작은 버튼으로만(모바일은 이 버튼이 사진첩) — 티켓 #445.
// 읽어낸 글자는 그 자리에서 고칠 수 있고, "전체 복사"는 고친 그대로 복사한다.
//
// 원칙(티켓 #436 범위):
//  - 한 번에 한 장. 새 이미지를 넣으면 앞의 결과는 지워지고 새로 읽는다.
//  - 창을 닫으면 이미지도 글자도 남지 않는다 — 어디에도 저장하지 않는다.
//  - 신청서 등 다른 칸에 자동으로 채워 넣지 않는다. 뽑아 고치고 복사하는 데까지다.
//
// 입력 UX(붙여넣기·드래그·클릭 첨부)는 구두정책 AI 인라인 추가(VerbalInlineAiAdd)의 핸들러를,
// 서버 전사는 /api/v2/tools/image-to-text(전사 전용 경량 경로)를 쓴다.
import { useEffect, useRef, useState } from "react";
import { authForm } from "../../api/_client";
import { ModalShell } from "../ui/modal";
import { useModal } from "../../hooks/useModal";
import { useToast } from "../common/Toast";

const API = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2/tools`;

type Phase =
  | { kind: "idle" }
  | { kind: "reading" }
  | { kind: "done"; text: string } // text="" 는 "글자를 못 찾음" 상태
  | { kind: "error"; message: string };

export default function ImageTextExtractor({ className = "" }: { className?: string }) {
  const toast = useToast();
  // 모달 열림은 URL(?modal=image-text)이 진본 — 뒤로가기·새로고침에도 규약대로 동작한다.
  const { isOpen, openModal, closeModal } = useModal("image-text");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  // 진행 중 요청 식별 — 새 이미지를 넣으면 앞 요청의 응답은 버린다(한 번에 한 장 규칙).
  const requestSeq = useRef(0);

  // 창을 닫으면 이미지도 글자도 남기지 않는다(티켓 #436 범위 규정).
  const reset = () => {
    requestSeq.current += 1;
    setPhase({ kind: "idle" });
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };
  // 닫히면(X·Esc·뒤로가기 어느 경로든) 이미지·글자를 즉시 비운다 — "창을 닫으면 남지 않는다".
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);
  // 언마운트 시 미리보기 URL 누수 방지.
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const acceptImageFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setPhase({ kind: "error", message: "이미지 파일(png·jpg)만 넣을 수 있습니다" });
      return;
    }
    // 새 이미지 = 앞의 결과는 지워지고 새로 읽는다.
    const seq = ++requestSeq.current;
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setPhase({ kind: "reading" });
    try {
      const form = new FormData();
      form.append("image", file);
      const res = await authForm<{ text: string | null }>(`${API}/image-to-text`, form);
      if (seq !== requestSeq.current) return; // 그 사이 새 이미지가 들어왔다 — 이 결과는 버린다
      setPhase({ kind: "done", text: res.text ?? "" });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      const message =
        err instanceof Error && err.message
          ? err.message
          : "지금은 읽지 못했습니다. 잠시 후 다시 시도해 주세요.";
      setPhase({ kind: "error", message });
    }
  };

  // 창이 열려 있는 동안엔 어디에 커서가 있든 Ctrl+V 로 이미지가 들어간다(티켓 #445).
  // 단 글자를 고치는 textarea·input 안에서의 붙여넣기는 가로채지 않는다 — 거긴 글자가 붙는 자리다.
  useEffect(() => {
    if (!isOpen) return;
    const onDocPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.closest("textarea, input") || target.isContentEditable)) return;
      const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith("image/"),
      );
      const file = item?.getAsFile();
      if (file) {
        e.preventDefault();
        void acceptImageFile(file);
      }
    };
    document.addEventListener("paste", onDocPaste);
    return () => document.removeEventListener("paste", onDocPaste);
    // acceptImageFile 은 안정적인 setter·ref 만 쓴다 — isOpen 전환 시에만 걸고 푼다.
  }, [isOpen]);
  const onDropFiles = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = Array.from(e.dataTransfer.files)[0];
    if (file && file.type.startsWith("image/")) void acceptImageFile(file);
    else setPhase({ kind: "error", message: "이미지 파일(png·jpg)만 넣을 수 있습니다" });
  };

  const copyAll = async () => {
    if (phase.kind !== "done") return;
    try {
      await navigator.clipboard.writeText(phase.text);
      toast.success("복사했습니다");
    } catch {
      toast.error("복사하지 못했습니다. 글자를 직접 선택해 복사해 주세요.");
    }
  };

  const hasResult = phase.kind === "done" || phase.kind === "reading" || phase.kind === "error";

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        title="이미지를 넣으면 안의 글자를 뽑아 복사할 수 있습니다"
        className={`h-9 shrink-0 whitespace-nowrap rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-gray-600 transition hover:border-brand-400 hover:text-brand-500 dark:border-gray-700 dark:text-gray-300 dark:hover:text-brand-400 ${className}`}
      >
        {/* 좁은 화면에선 아이콘만 — 버튼은 어느 폭에서도 늘 보인다(티켓 #436). */}
        <span aria-hidden>🖼</span>
        {/* 2026-08-28: 글자를 보이는 기준을 lg → 2xl 로 올렸다. 상단 띠에 만나이·할 일이
            들어오면서 1440 에서 이 글자 132px 이 줄바꿈을 만들었다. 아이콘만 남아도 말풍선이
            무엇인지 말해 준다. */}
        <span className="ml-1 hidden 2xl:inline">이미지에서 글자 뽑기</span>
      </button>

      <ModalShell
        isOpen={isOpen}
        onClose={closeModal}
        title="🖼 이미지에서 글자 뽑기"
        subtitle="이미지 속 글자를 읽어 그대로 옮겨 드립니다 — 창을 닫으면 어디에도 남지 않습니다"
        size="lg"
      >
        {/* 입력 칸 — 끌어다 놓기 · Ctrl+V(문서 어디서든). 박스 클릭은 탐색기를 열지 않는다(티켓 #445);
            파일 선택은 오른쪽 아래 "파일에서 고르기" 작은 버튼으로만 — 폰에선 이 버튼이 사진첩을 연다. */}
        <div
          ref={dropRef}
          tabIndex={0}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDropFiles}
          className={`relative flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 pb-11 pt-6 text-center outline-none transition focus:ring-2 focus:ring-brand-200 dark:focus:ring-brand-500/30 ${
            dragOver
              ? "border-brand-500 bg-brand-50/70 dark:bg-brand-500/10"
              : "border-gray-300 bg-gray-50 hover:border-brand-400 dark:border-gray-700 dark:bg-gray-800/60"
          }`}
        >
          <span className="text-2xl" aria-hidden>
            🖼
          </span>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            이미지를 끌어다 놓거나, 여기를 누르고 Ctrl+V로 붙여넣으세요
          </p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            파일에서 고르려면 오른쪽 아래 버튼을 누르세요 · 한 번에 한 장 — 새로 넣으면 앞 결과는 지워집니다
          </p>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="absolute bottom-2 right-2 h-8 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-600 hover:border-brand-400 hover:text-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
          >
            파일에서 고르기
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void acceptImageFile(f);
            e.target.value = "";
          }}
        />

        {hasResult && (
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            {/* 넣은 이미지 미리보기 */}
            {previewUrl && (
              <img
                src={previewUrl}
                alt="넣은 이미지 미리보기"
                className="max-h-56 w-full rounded-lg border border-gray-200 object-contain dark:border-gray-700 sm:w-48 sm:shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              {phase.kind === "reading" && (
                <div className="flex h-full min-h-24 items-center justify-center rounded-lg border border-gray-200 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  글자를 읽는 중…
                </div>
              )}
              {phase.kind === "error" && (
                <div className="flex h-full min-h-24 items-center justify-center rounded-lg border border-rose-200 bg-rose-50/60 px-4 text-center text-sm text-rose-600 dark:border-rose-500/40 dark:bg-rose-500/[0.08] dark:text-rose-300">
                  {phase.message}
                </div>
              )}
              {phase.kind === "done" && phase.text === "" && (
                <div className="flex h-full min-h-24 items-center justify-center rounded-lg border border-amber-200 bg-amber-50/60 px-4 text-center text-sm text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/[0.08] dark:text-amber-300">
                  이미지에서 글자를 찾지 못했습니다. 더 또렷한 사진으로 다시 해보세요.
                </div>
              )}
              {phase.kind === "done" && phase.text !== "" && (
                <>
                  <textarea
                    value={phase.text}
                    onChange={(e) => setPhase({ kind: "done", text: e.target.value })}
                    className="min-h-40 w-full resize-y rounded-lg border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    aria-label="읽어낸 글자 — 그 자리에서 고칠 수 있습니다"
                  />
                  <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                    읽어낸 글자는 여기서 바로 고칠 수 있고, 전체 복사는 고친 그대로 복사됩니다.
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={copyAll}
                      className="h-9 rounded-lg bg-brand-500 px-4 text-sm font-semibold text-white hover:bg-brand-600"
                    >
                      전체 복사
                    </button>
                    <button
                      type="button"
                      onClick={reset}
                      className="h-9 rounded-lg border border-gray-200 px-3 text-sm font-medium text-gray-600 hover:border-brand-400 hover:text-brand-500 dark:border-gray-700 dark:text-gray-300"
                    >
                      다시 넣기
                    </button>
                  </div>
                </>
              )}
              {(phase.kind === "error" || (phase.kind === "done" && phase.text === "")) && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={reset}
                    className="h-9 rounded-lg border border-gray-200 px-3 text-sm font-medium text-gray-600 hover:border-brand-400 hover:text-brand-500 dark:border-gray-700 dark:text-gray-300"
                  >
                    다시 넣기
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </ModalShell>
    </>
  );
}
